import {
	createFileRoute,
	stripSearchParams,
	useNavigate,
	useRouteContext,
	useSearch,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { cursorToOffset, offsetToCursor } from "graphql-relay";
import { useEffect, useTransition } from "react";
import { graphql, useLazyLoadQuery, useRefetchableFragment } from "react-relay";
import z from "zod";
import { TemplatesTable } from "@/components/tables/templates";
import type { templatesDeEmailPageFragment$key } from "./__generated__/templatesDeEmailPageFragment.graphql";
import type { templatesDeEmailQuery } from "./__generated__/templatesDeEmailQuery.graphql";
import type { templatesDeEmailTableQuery } from "./__generated__/templatesDeEmailTableQuery.graphql";

const defaultValues = {
	page: 1,
} as const;

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/automacoes/templates-de-email",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			page: z.number().min(1).default(1),
		}),
	),
	search: {
		middlewares: [stripSearchParams(defaultValues)],
	},
});

function RouteComponent() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const { page } = useSearch({ from: Route.id });
	const [isPending, startTransition] = useTransition();
	const navigate = useNavigate({
		from: Route.fullPath,
	});
	const data = useLazyLoadQuery<templatesDeEmailQuery>(
		graphql`
			query templatesDeEmailQuery($slug: String!, $first: Int, $last: Int, $after: String, $before: String) {
				club(slug: $slug) {
					...templatesDeEmailPageFragment @arguments(first: $first, last: $last, after: $after, before: $before)
				}
			}
		`,
		{
			slug: club?.slug,
			first: 15,
			after: page === 1 ? undefined : offsetToCursor((page - 1) * 15 - 1),
		},
	);

	const [templates, refetch] = useRefetchableFragment<
		templatesDeEmailTableQuery,
		templatesDeEmailPageFragment$key
	>(
		graphql`
			fragment templatesDeEmailPageFragment on Club
				@refetchable(queryName: "templatesDeEmailTableQuery")
				@argumentDefinitions(
					first: { type: "Int" }
					last: { type: "Int" }
					after: { type: "String" }
					before: { type: "String" }
				) {
				templates(first: $first, after: $after, before: $before, last: $last) {
					edges {
						node {
							id
							title
							createdAt
							updatedAt
							createdBy {
								displayName
								avatarFallback
								photo {
									signedUrl
								}
							}
						}
						cursor
					}
					pageInfo {
						hasNextPage
						hasPreviousPage
						totalCount
						startCursor
						endCursor
					}
				}
			}
		`,
		data.club,
	);

	useEffect(() => {
		startTransition(() => {
			refetch(
				{
					first: 15,
					after: page === 1 ? undefined : offsetToCursor((page - 1) * 15 - 1),
				},
				{ fetchPolicy: "store-or-network" },
			);
		});
	}, [page, refetch]);
	const refreshTemplates = () => {
		startTransition(() => {
			refetch({}, { fetchPolicy: "network-only" });
		});
	};

	return (
		<div className="p-6">
			<TemplatesTable
				data={
					templates?.templates?.edges.filter(Boolean).map(({ node }) => node) ||
					[]
				}
				isPending={isPending}
				onDeleted={refreshTemplates}
				totalCount={templates?.templates.pageInfo.totalCount || 0}
				hasNextPage={Boolean(templates?.templates.pageInfo.hasNextPage)}
				hasPreviousPage={Boolean(templates?.templates.pageInfo.hasPreviousPage)}
				loadNext={() =>
					startTransition(() => {
						navigate({ search: (s) => ({ ...s, page: s.page + 1 }) });
					})
				}
				loadPrevious={() =>
					startTransition(() => {
						navigate({ search: (s) => ({ ...s, page: s.page - 1 }) });
					})
				}
				startIndex={
					templates?.templates.edges[0]
						? cursorToOffset(templates?.templates.edges[0].cursor) + 1
						: 1
				}
				endIndex={
					templates?.templates.edges[templates?.templates.edges.length - 1]
						? cursorToOffset(
								templates?.templates.edges[
									templates?.templates.edges.length - 1
								].cursor,
							) + 1
						: 1
				}
			/>
		</div>
	);
}
